import React, { useCallback, useEffect, useMemo } from "react";
import Button from "./Button";
import Modal from "./Modal";

const NumericKeypadModal = ({
  isOpen,
  title = "Enter Value",
  value = "",
  onChange,
  onClose,
  allowDecimal = true,
}) => {
  const displayValue = useMemo(() => {
    if (value === "" || value === null || value === undefined) return "0";
    return String(value);
  }, [value]);

  const appendValue = useCallback(
    (key) => {
      const current = String(value ?? "");

      if (key === "C") {
        onChange("");
        return;
      }

      if (key === "DEL") {
        onChange(current.slice(0, -1));
        return;
      }

      if (key === "." && !allowDecimal) {
        return;
      }

      if (key === ".") {
        if (current.includes(".")) return;
        onChange(current ? `${current}.` : "0.");
        return;
      }

      if (/^\d$/.test(key)) {
        if (current === "0") {
          onChange(key);
          return;
        }
        onChange(`${current}${key}`);
      }
    },
    [allowDecimal, onChange, value],
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      const { key } = event;

      if (/^\d$/.test(key)) {
        event.preventDefault();
        appendValue(key);
        return;
      }

      if (key === "." || key === "Decimal") {
        event.preventDefault();
        appendValue(".");
        return;
      }

      if (key === "Backspace" || key === "Delete") {
        event.preventDefault();
        appendValue("DEL");
        return;
      }

      if (key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (key === "Enter") {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [appendValue, isOpen, onClose]);

  const keyRows = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["DEL", "0", "."],
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="md"
      showCancel={false}
      zIndexClassName="z-[70]"
    >
      <div className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-right shadow-sm dark:border-slate-700 dark:bg-slate-900/60">
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Current Value
          </p>
          <p className="mt-1 break-all text-3xl font-semibold leading-tight text-slate-900 dark:text-slate-100">
            {displayValue}
          </p>
        </div>

        <div className="space-y-3">
          {keyRows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className={row.length === 4 ? "grid grid-cols-4 gap-3" : "grid grid-cols-3 gap-3"}
            >
              {row.map((key) => {
                const isDecimalKey = key === ".";
                if (isDecimalKey && !allowDecimal) return null;

                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => appendValue(key)}
                    className={[
                      "h-14 rounded-xl border text-lg font-semibold transition active:scale-[0.98]",
                      key === "DEL"
                        ? "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-200"
                        : "border-slate-200 bg-white text-slate-900 hover:border-indigo-300 hover:bg-indigo-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-indigo-700 dark:hover:bg-indigo-950/30",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {key}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default NumericKeypadModal;
